import _ from 'lodash';
import moment from 'moment';
import { MyXLSX2 } from '@/utils/xlsxExport2';
import PROJECT_EXPORT_FIELDS from './ProjectExportFields';

let CD;

export default {
    created() {
        CD = moment().format('YYYY-MM-DD');
    },
    methods: {
        async onExport(selectedFieldKeys) {
            const wb = MyXLSX2.createBook();
            this.isLoadingExport = true;

            try {
                const allProjectSheet = await this.getAllProjectSheet(selectedFieldKeys);
                if (allProjectSheet) MyXLSX2.bookAppendSheet(wb, allProjectSheet, '全部');
                MyXLSX2.download(wb, `專案管理${CD}`);
                this.exportExcelDialog.visible = false;
            } catch (e) {
                console.log(e);
                this.$message({
                    showClose: true,
                    message: '無資料可匯出',
                    type: 'error',
                });
            } finally {
                this.isLoadingExport = false;
            }
        },

        getExportFieldValue(item, key) {
            switch (key) {
            case 'poo': return item.poo || '';
            case 'customnumber': return item.customnumber || '';
            case 'status': return item.stG_STS === 1 ? '結案' : '進行中';
            case 'pjcaT_NAM': return item.pjcaT_NAM || '--';
            case 'nam': return item.nam || '';
            case 'vdR_NAM': return item.vdR_NAM || '--';
            case 'sdt': return item.sdt ? moment(item.sdt).format('YYYY-MM-DD') : '';
            case 'edt': return item.edt ? moment(item.edt).format('YYYY-MM-DD') : '';
            case 'timeProgress': return `${this.getTimeProgress(item)}%`;
            case 'installmentCount': return `共${item.installmentCount}期`;
            case 'installmentInvoicedCount': return `${item.installmentInvoicedCount}期`;
            case 'stF_NAM': return item.stF_NAM || '';
            case 'projectAuditorName': return item.projectAuditorName || '';
            case 'cny': return item.cny || '';
            case 'amt': return item.amt ?? 0;
            case 'cnyRat': return this.getCnyRat(item.cny);
            case 'twdAmt': return item.amt * this.getCnyRat(item.cny);
            case 'amT_PREOUT': return item.amT_PREOUT || 0;
            case 'totalPay': return item.totalPay || 0;
            case 'profitProgress': return item.amT_PREOUT ? `${this.getProfitProgress(item)}%` : '--';
            case 'disAmt': return this.getDisAmt(item);
            case 'actualGrossProfit': return this.getActualGrossProfit(item);
            case 'actualGrossMargin': return `${this.getActualGrossMargin(item)}%`;
            default: return '';
            }
        },

        getExportFieldTotal(key, details) {
            switch (key) {
            case 'twdAmt': return _.sumBy(details, (o) => o.amt * this.getCnyRat(o.cny));
            case 'amT_PREOUT': return _.sumBy(details, (o) => o.amT_PREOUT || 0);
            case 'totalPay': return _.sumBy(details, (o) => o.totalPay || 0);
            default: return '';
            }
        },

        async getAllProjectSheet(selectedFieldKeys) {
            const details = await this.getExportData({
                ...this.queryParams,
                searchPMs: undefined,
                cny: undefined,
                queryStartDate: undefined,
                queryEndDate: undefined,
            });
            if (!details.length) return null;

            const selectedFields = PROJECT_EXPORT_FIELDS.filter((f) => selectedFieldKeys.includes(f.key));
            if (!selectedFields.length) return null;

            // 內容表頭
            const bodyHeader = selectedFields.map((f) => ({
                text: f.label, width: f.width, align: f.align, number: f.number,
            }));

            // 內容資料
            const bodyRows = details.map((item) => selectedFields.map((f) => this.getExportFieldValue(item, f.key)));

            // 總計列
            bodyRows.push(selectedFields.map((f, i) => (i === 0 ? '總計(約當台幣)' : this.getExportFieldTotal(f.key, details))));

            return MyXLSX2.createSheet(null, [], bodyHeader, bodyRows);
        },
        async getExportData(params) {
            const PageSize = 1000;
            let PageNum = 1;
            const {
                StartDate, EndDate, ProjectEndDateStart, ProjectEndDateEnd,
            } = this.setupTimeFormat();

            const { data } = await this.$api({
                url: `ProjectManagementPage/GetProjectDetailList${this.getManualQuery(this.queryParams)}`,
                method: 'GET',
                params: {
                    ...params,
                    PageNum: 1,
                    PageSize,
                    pjStartDate: undefined,
                    pjEndDate: undefined,
                    searchPMs: undefined,
                    VDR_SNOs: undefined,
                    PJCAT_SNOs: undefined,
                    StartDate,
                    EndDate,
                    ProjectEndDateStart,
                    ProjectEndDateEnd,
                },
            });

            // 足夠的PageSize
            if (PageSize > data.totalNum) {
                return data.items;
            }

            const currentList = [...data.items];
            for (let i = 0; i < Math.ceil(data.totalNum / PageSize) - 1; i += 1) {
                PageNum += 1;
                // eslint-disable-next-line no-await-in-loop
                const resp = await this.$api({
                    url: `ProjectManagementPage/GetProjectDetailList${this.getManualQuery(this.queryParams)}`,
                    method: 'GET',
                    params: {
                        ...params,
                        PageNum,
                        PageSize,
                        pjStartDate: undefined,
                        pjEndDate: undefined,
                        searchPMs: undefined,
                        VDR_SNOs: undefined,
                        PJCAT_SNOs: undefined,
                        StartDate,
                        EndDate,
                        ProjectEndDateStart,
                        ProjectEndDateEnd,
                    },
                });
                currentList.push(...resp.data.items);
            }
            return currentList;
        },

        getEstimateGrossMargin(item) {
            const RATE = this.getCnyRat(item.cny);
            const PROFIT = item.amt * RATE - item.amT_PREOUT;

            if (!item.amt * RATE) return '--';
            if (PROFIT) return Math.round((PROFIT / (item.amt * RATE)) * 1000) / 10;
            return 0;
        },
        getActualGrossMargin(item) {
            const RATE = this.getCnyRat(item.cny);
            const PROFIT = (item.amt - this.getDisAmt(item)) * RATE - item.totalPay;

            if (!item.amt * RATE) return '--';
            if (PROFIT) return Math.round((PROFIT / ((item.amt - this.getDisAmt(item)) * RATE)) * 1000) / 10;
            return 0;
        },
        getDisAmt(item) {
            let disAmt = 0;
            item.installments.forEach((ins) => {
                if (!ins.foO_DIS_CML) disAmt += ins.foO_DIS_AMT;
            });
            return disAmt;
        },
        getActualGrossProfit(item) {
            const RATE = this.getCnyRat(item.cny);
            const PROFIT = (item.amt - this.getDisAmt(item)) * RATE - item.totalPay;

            if (!item.amt * RATE) return '--';
            if (PROFIT) return PROFIT;
            return 0;
        },
        getCnyRat(cny) {
            const cnyOption = this.$store.getters['cny/cnyOptions'].find((x) => x.cny === cny);
            if (cnyOption) return cnyOption.rat;
            return 1;
        },
        getTimeProgress(item) {
            const start = item.sdt;
            const end = item.edt;
            const today = new Date().getTime();

            if (end === start) return 100;
            if (end < start) return 0;
            if (today < start) return 0;
            if (today >= end) return 100;

            return Math.round(((today - start) / (end - start)) * 100);
        },
        getProfitProgress(item) {
            if (item.totalPay === null || item.totalPay === 0 || item.amT_PREOUT === null || item.amT_PREOUT === 0) {
                return 0;
            }
            return Math.round((item.totalPay / item.amT_PREOUT) * 100);
        },
    },
};
